import React, { useState, useEffect } from 'react';
import {
  ChevronDownIcon,
  LockClosedIcon,
  SparklesIcon,
  ArrowPathIcon,
  TableCellsIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const FUNNEL_LEVELS = ['Unaware', 'Problem Aware', 'Solution Aware', 'Product Aware', 'Most Aware'];

const FUNNEL_COLORS = {
  'Unaware': '#4C6FFF',
  'Problem Aware': '#8B5CF6',
  'Solution Aware': '#EC4899',
  'Product Aware': '#F97316',
  'Most Aware': '#FBBF24',
};

const FUNNEL_META = {
  'Unaware': { tag: 'Cold', hint: "Doesn't know they have the problem yet" },
  'Problem Aware': { tag: 'Cooling', hint: 'Feels the problem, not the fix' },
  'Solution Aware': { tag: 'Warm', hint: 'Comparing solutions like yours' },
  'Product Aware': { tag: 'Hot', hint: 'Knows your product specifically' },
  'Most Aware': { tag: 'Ready', hint: 'Just needs the right nudge to buy' },
};

const FUNNEL_WIDTHS = ['100%', '87%', '74%', '61%', '48%'];

function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function DifficultyBadge({ value }) {
  if (value === undefined || value === null || value === '') {
    return <span className="text-white/25">—</span>;
  }
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) {
    return <span className="font-mono text-xs text-white/55">{value}</span>;
  }
  const color = num < 34 ? '#34D399' : num < 67 ? '#FBBF24' : '#F87171';
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-xs text-white/80">
      <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
      {num}
    </span>
  );
}

function Volume({ value }) {
  if (value === undefined || value === null || value === '') {
    return <span className="text-white/25">—</span>;
  }
  const num = typeof value === 'number' ? value : parseFloat(value);
  return (
    <span className="font-mono text-xs text-white/80">
      {!isNaN(num) ? num.toLocaleString() : value}
    </span>
  );
}

export default function Funnel({ companyCode, industry, description, onGenerate, credits: initialCredits }) {
  const [funnelData, setFunnelData] = useState({});
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [credits, setCredits] = useState(initialCredits || 0);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFunnelData();
    if (initialCredits === undefined) fetchCredits();
  }, []);

  const fetchFunnelData = async () => {
    setLoading(true);
    try {
      const res = await fetch('https://api.insightos.co.uk/api/funnel', { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setFunnelData(data.data || {});
    } catch {
      setError('Failed to load funnel data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCredits = async () => {
    try {
      const res = await fetch('https://api.insightos.co.uk/leads/company', { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setCredits(data.data[0].SalesCredits);
    } catch {}
  };

  const handleGenerate = async () => {
    if (credits < 10) {
      setError('You need at least 10 credits to generate funnel suggestions.');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const res = await fetch('https://api.insightos.co.uk/api/funnel/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ industry, description }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Generation failed');
      setFunnelData(data.data || {});
      setCredits((prev) => prev - 10);
      if (onGenerate) onGenerate(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const toggleLevel = (level) => {
    if (!funnelData[level] || (!funnelData[level].keywords?.length && !funnelData[level].content?.length)) return;
    setSelectedLevel(selectedLevel === level ? null : level);
  };

  const hasData = (level) => {
    return funnelData && funnelData[level] && (funnelData[level].keywords?.length || funnelData[level].content?.length);
  };

  return (
    <div className="w-full max-w-3xl mx-auto rounded-3xl border p-6 sm:p-8" style={{
      background: 'radial-gradient(120% 140% at 50% -10%, #171D2C 0%, #0B0E14 55%)',
      borderColor: 'rgba(255,255,255,0.06)',
    }}>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
          .funnel-display { font-family: 'Space Grotesk', ui-sans-serif, system-ui, sans-serif; }
          .funnel-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
          .funnel-flow-line {
            background: repeating-linear-gradient(to bottom, rgba(255,255,255,0.85) 0 5px, transparent 5px 15px);
            background-size: 100% 30px;
            animation: funnel-flow-move 1.4s linear infinite;
          }
          @keyframes funnel-flow-move { to { background-position: 0 30px; } }
          .funnel-shimmer {
            background: linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.09) 37%, rgba(255,255,255,0.04) 63%);
            background-size: 400% 100%;
            animation: funnel-shimmer-move 1.6s ease-in-out infinite;
          }
          @keyframes funnel-shimmer-move { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }
          @media (prefers-reduced-motion: reduce) {
            .funnel-flow-line, .funnel-shimmer { animation: none; }
          }
          .funnel-expand-content {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.4s ease-out;
          }
          .funnel-expand-content.open {
            max-height: 6000px; /* ✅ increased to fit all content */
            transition: max-height 0.6s ease-in;
          }
        `}
      </style>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <div>
          <p className="funnel-mono text-[11px] tracking-[0.2em] uppercase mb-2 text-white/35">
            Awareness pipeline
          </p>
          <h2 className="funnel-display text-2xl font-semibold text-[#F1F4F9]">
            Marketing funnel
          </h2>
          <p className="text-sm mt-1 text-white/50">
            Cold traffic, mapped stage by stage into ready buyers.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="funnel-mono flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-white/10 bg-white/5">
            <span className={`h-1.5 w-1.5 rounded-full ${credits >= 10 ? 'bg-emerald-400' : 'bg-red-400'}`} />
            <span className={credits >= 10 ? 'text-emerald-400' : 'text-red-400'}>
              {credits.toLocaleString()} credits
            </span>
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating || loading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-opacity hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              color: '#0B0E14',
              background: generating || loading
                ? 'rgba(255,255,255,0.08)'
                : 'linear-gradient(90deg, #4C6FFF, #EC4899 55%, #FBBF24)',
              boxShadow: generating || loading ? 'none' : '0 8px 24px -8px rgba(236,72,153,0.5)',
            }}
          >
            {generating ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin text-white/60" />
                <span className="text-white/60">Generating…</span>
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4" />
                Generate ideas
                <span className="funnel-mono opacity-70">10cr</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Heat legend */}
      <div className="flex items-center gap-3 mt-5 mb-6">
        <span className="text-[11px] funnel-mono text-white/30">Cold</span>
        <div className="flex-1 h-1 rounded-full bg-gradient-to-r from-[#4C6FFF] via-[#8B5CF6] via-[#EC4899] via-[#F97316] to-[#FBBF24]" />
        <span className="text-[11px] funnel-mono text-white/30">Hot</span>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-xl text-sm border border-red-200/20 bg-red-500/10 text-red-300">
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="flex flex-col items-center gap-4">
          {FUNNEL_WIDTHS.map((w, i) => (
            <div
              key={i}
              style={{ width: w }}
              className="funnel-shimmer h-16 rounded-2xl border border-white/10"
            />
          ))}
        </div>
      )}

      {/* Funnel */}
      {!loading && (
        <div className="relative flex flex-col items-center gap-6 pt-1">
          <div
            className="absolute left-1/2 -translate-x-1/2 top-3 bottom-3 w-[2px] rounded-full pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, #4C6FFF, #8B5CF6, #EC4899, #F97316, #FBBF24)',
              opacity: 0.35,
            }}
          />
          <div className="funnel-flow-line absolute left-1/2 -translate-x-1/2 top-3 bottom-3 w-[2px] pointer-events-none" />

          {FUNNEL_LEVELS.map((level, index) => {
            const color = FUNNEL_COLORS[level];
            const meta = FUNNEL_META[level];
            const isSelected = selectedLevel === level;
            const levelData = funnelData[level] || { keywords: [], content: [] };
            const hasContent = levelData.keywords?.length > 0 || levelData.content?.length > 0;
            const width = FUNNEL_WIDTHS[index];

            return (
              <div key={level} style={{ width }} className="relative z-10">
                <div
                  className="absolute -top-[9px] left-1/2 -translate-x-1/2 h-4 w-4 rounded-full"
                  style={{
                    background: color,
                    boxShadow: `0 0 0 4px #0B0E14, 0 0 16px ${hexToRgba(color, 0.6)}`,
                  }}
                />

                <div
                  onClick={() => toggleLevel(level)}
                  className="mt-3 rounded-2xl border overflow-hidden backdrop-blur-sm transition-all duration-200 hover:brightness-110 cursor-pointer"
                  style={{
                    borderColor: hasContent ? hexToRgba(color, 0.35) : 'rgba(255,255,255,0.07)',
                    background: hasContent ? hexToRgba(color, 0.06) : 'rgba(255,255,255,0.015)',
                    boxShadow: isSelected ? `0 0 32px -8px ${hexToRgba(color, 0.35)}` : 'none',
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="funnel-display text-[15px] font-semibold text-[#F1F4F9]">
                            {level}
                          </h3>
                          <span
                            className="funnel-mono text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wide"
                            style={{ background: hexToRgba(color, 0.15), color }}
                          >
                            {meta.tag}
                          </span>
                        </div>
                        <p className="text-xs mt-1 text-white/45">
                          {meta.hint}
                        </p>
                      </div>

                      {hasContent ? (
                        <ChevronDownIcon
                          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 text-white/40 ${
                            isSelected ? 'rotate-180' : ''
                          }`}
                        />
                      ) : (
                        <LockClosedIcon className="h-4 w-4 flex-shrink-0 text-white/20" />
                      )}
                    </div>

                    {hasContent ? (
                      <div className="flex gap-4 mt-3">
                        <span className="funnel-mono text-[11px] text-white/50">
                          {levelData.keywords?.length || 0} keywords
                        </span>
                        <span className="funnel-mono text-[11px] text-white/50">
                          {levelData.content?.length || 0} content ideas
                        </span>
                      </div>
                    ) : (
                      <div className="inline-block mt-3 text-[11px] px-2 py-1 rounded-full border border-dashed border-white/10 text-white/30">
                        Generate to unlock
                      </div>
                    )}
                  </div>

                  {/* ✅ Expandable content with scroll */}
                  <div className={`funnel-expand-content ${isSelected ? 'open' : ''}`}>
                    <div className="p-4 border-t border-white/10 max-h-[70vh] overflow-y-auto">
                      {levelData.keywords?.length > 0 && (
                        <div className="mb-5">
                          <h4 className="funnel-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 mb-2 text-white/40">
                            <TableCellsIcon className="h-3.5 w-3.5" /> Keywords
                          </h4>
                          <div className="overflow-x-auto rounded-lg border border-white/5">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="px-3 py-2 text-left funnel-mono text-[10px] uppercase tracking-wide text-white/40">
                                    Keyword
                                  </th>
                                  <th className="px-3 py-2 text-left funnel-mono text-[10px] uppercase tracking-wide text-white/40">
                                    Volume
                                  </th>
                                  <th className="px-3 py-2 text-left funnel-mono text-[10px] uppercase tracking-wide text-white/40">
                                    Difficulty
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {levelData.keywords.map((kw, idx) => (
                                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                    <td className="px-3 py-2 text-white/85">{kw.keyword}</td>
                                    <td className="px-3 py-2"><Volume value={kw.volume} /></td>
                                    <td className="px-3 py-2"><DifficultyBadge value={kw.difficulty} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {levelData.content?.length > 0 && (
                        <div>
                          <h4 className="funnel-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 mb-2 text-white/40">
                            <DocumentTextIcon className="h-3.5 w-3.5" /> Content suggestions
                          </h4>
                          <div className="space-y-2">
                            {levelData.content.map((item, idx) => (
                              <div
                                key={idx}
                                className="p-3 rounded-lg bg-white/5"
                                style={{ borderLeft: `2px solid ${color}` }}
                              >
                                <p className="text-sm font-medium text-white/90">{item.title}</p>
                                <p className="text-xs mt-1 text-white/50">{item.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}